import { Component } from 'react';
import { observable, action, makeObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import apiService from '@services/apiService';
import { BlockLoader } from '@components/BlockLoader.tsx';
import { modalService } from '@components/Modal';
import ToolDefinitionDialog from '@components/ToolDefinitionDialog';

import { ImgDocumentCheck } from '@images/ImgDocumentCheck';
import { ImgImage } from '@images/ImgImage';
import { ImgTools } from '@images/ImgTools';
import { ImgWeather } from '@images/ImgWeather';
import { ImgWeb } from '@images/ImgWeb';
import { ImgChevronRight } from '@images/ImgChevronRight';
import { ImgCodeBraces } from '@images/ImgCodeBraces';

import './tools-selector.css';
import { legacyToActualMap } from '@root/models';
import type { McpToolAllowMode } from '@root/models';
import { formatTokenCount } from '@root/utils/format.ts';

// Icons as simple SVG components
const ChevronRight = ({ expanded }: { expanded: boolean }) => (
  <ImgChevronRight size={16} expanded={expanded} />
);

export type OllamaToolResponse = {
  type: 'function';
  toolSetName?: string;
  category?: string;
  icon?: string;
  tokens?: number;
  /** Present for MCP tools */
  mcpServerId?: string;
  /** MCP approval mode; null/absent = not set (behaves as 'ask') */
  allowMode?: McpToolAllowMode | null;
  function: {
    name: string;
    description: string;
    parameters: {
      type: 'object';
      properties: Record<string, { type: string; description: string }>;
      required: string[];
    };
  };
};

// Tool icons (simplified)
const ToolIcon = ({ icon }: { icon?: string; }) => {
  if (icon === 'weather') {
    return <span className="tool-icon">
      <ImgWeather size={16} />
    </span>;
  }
  if (icon === 'web'){
    return <span className="tool-icon">
      <ImgWeb size={16} />
    </span>;
  }
  if (icon === 'image'){
    return <span className="tool-icon">
      <ImgImage size={16} />
    </span>;
  }
  if (icon === 'document'){
    return <span className="tool-icon">
      <ImgDocumentCheck size={16} />
    </span>;
  }

  return <span className="tool-icon">
    <ImgTools size={14} />
  </span>;
};

type ToolItem = {
  id: string;
  name: string;
  description: string;
  icon: string;
  tokens?: number;
  /** True for MCP tools — shows the approval-mode control */
  isMcp: boolean;
  /** null = not set (behaves as 'ask') */
  allowMode: McpToolAllowMode | null;
  /** Full tool definition (name/description/parameters) for the "view definition" dialog */
  definition?: OllamaToolResponse;
}

type SubCategory = {
  id: string;
  name: string;
  icon?: string;
  items: ToolItem[];
  expanded: boolean;
  selected: boolean;
}

type Category = {
  id: string;
  name: string;
  icon?: string;
  subCategories: SubCategory[];
  expanded: boolean;
  selected: boolean;
}

class ChatToolsTabStore {
  @observable categories: Category[] = [];
  @observable selectedTools: string[];
  @observable isLoading: boolean = true;
  public loader: PromiseCompletion;
  private onChange?: (selectedTools: string[]) => void;

  constructor({ tools, loader, onChange }: { tools?: string[] | null , loader: PromiseCompletion, onChange?: (selectedTools: string[]) => void }) {
    const mappedTools = tools?.map(t => legacyToActualMap[t] || t);
    this.selectedTools = mappedTools || [];
    this.loader = loader;
    this.onChange = onChange;
    makeObservable(this);
    void this._loadTools(mappedTools);
  }

  private async _loadTools(selectedTools?: string[] | null){
    const { tools } = await apiService.get<{tools: OllamaToolResponse[]}>('/api/tools?includeTokens=true');
    runInAction(() => {
      const categoryMap = new Map<string, Category>();

      tools.forEach((tool) => {
        const categoryName = tool.toolSetName || 'Built-In Tools';
        const categoryId = categoryName.toLowerCase().replace(/\s+/g, '-');
        const subCategoryName = tool.category || 'General';
        const subCategoryId = `${categoryId}-${subCategoryName.toLowerCase().replace(/\s+/g, '-')}`;
        
        // Get or create category
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, {
            id: categoryId,
            name: categoryName,
            icon: 'default',
            subCategories: [],
            expanded: categoryName === 'Built-In Tools',
            selected: false
          });
        }
        
        const category = categoryMap.get(categoryId)!;
        
        // Get or create subcategory
        let subCategory = category.subCategories.find(sc => sc.id === subCategoryId);
        if (!subCategory) {
          subCategory = {
            id: subCategoryId,
            name: subCategoryName,
            icon: tool.icon || 'default',
            items: [],
            expanded: category.expanded,
            selected: false
          };
          category.subCategories.push(subCategory);
        }
        
        // Add tool item
        const toolItem: ToolItem = {
          id: tool.function.name,
          name: tool.function.name,
          description: tool.function.description,
          icon: tool.icon || 'default',
          tokens: tool.tokens,
          isMcp: !!tool.mcpServerId,
          allowMode: tool.mcpServerId ? (tool.allowMode ?? null) : null,
          definition: tool
        };
        subCategory.items.push(toolItem);
      });

      this.categories = Array.from(categoryMap.values());
      this.isLoading = false;

      if (!selectedTools) {
        this.selectedTools = tools.map(t => t.function.name);
      }
      
      // Update selection states based on initially selected items
      this.categories.forEach(category => {
        category.subCategories.forEach(subCategory => {
          this.updateSubCategoryState(subCategory);
        });
        this.updateCategoryState(category);
      });
    });
  }

  @action
  toggleCategory(categoryId: string) {
    const category = this.categories.find(c => c.id === categoryId);
    if (category) {
      category.expanded = !category.expanded;
    }
  }

  @action
  toggleSubCategory(categoryId: string, subCategoryId: string) {
    const category = this.categories.find(c => c.id === categoryId);
    if (category) {
      const subCategory = category.subCategories.find(sc => sc.id === subCategoryId);
      if (subCategory) {
        subCategory.expanded = !subCategory.expanded;
      }
    }
  }

  @action
  toggleCategorySelection(categoryId: string) {
    const category = this.categories.find(c => c.id === categoryId);
    if (category) {
      const newState = !category.selected;
      category.selected = newState;
      category.subCategories.forEach(sc => {
        sc.selected = newState;

        if (newState) {
          sc.items.forEach(item => {
            if (!this.selectedTools.includes(item.id)) {
              this.selectedTools.push(item.id);
            }
          });
        } else {
          sc.items.forEach(item => {
            this.selectedTools = this.selectedTools.filter(t => t !== item.id);
          });
        }
      });
      this.onChange?.(this.selectedTools);
    }
  }

  @action
  toggleSubCategorySelection(categoryId: string, subCategoryId: string) {
    const category = this.categories.find(c => c.id === categoryId);
    if (category) {
      const subCategory = category.subCategories.find(sc => sc.id === subCategoryId);
      if (subCategory) {
        const newState = !subCategory.selected;
        subCategory.selected = newState;
        subCategory.items.forEach(item => {
          if (newState) {
            if (!this.selectedTools.includes(item.id)) {
              this.selectedTools.push(item.id);
            }
          } else {
            this.selectedTools = this.selectedTools.filter(t => t !== item.id);
          }
        });

        this.updateCategoryState(category);
        this.onChange?.(this.selectedTools);
      }
    }
  }

  @action
  toggleItemSelection(categoryId: string, subCategoryId: string, itemId: string) {
    const category = this.categories.find(c => c.id === categoryId);
    if (category) {
      const subCategory = category.subCategories.find(sc => sc.id === subCategoryId);
      if (subCategory) {
        const item = subCategory.items.find(i => i.id === itemId);
        if (item) {
          const isSelected = this.selectedTools.includes(item.id);
          if (isSelected) {
            this.selectedTools = this.selectedTools.filter(t => t !== item.id);
          } else {
            this.selectedTools.push(item.id);
          }          

          this.updateSubCategoryState(subCategory);
          this.updateCategoryState(category);
          this.onChange?.(this.selectedTools);
        }
      }
    }
  }

  @action
  updateSubCategoryState(subCategory: SubCategory) {
    const allSelected = subCategory.items.every(i => this.selectedTools.includes(i.id));
    const someSelected = subCategory.items.some(i => this.selectedTools.includes(i.id));
    subCategory.selected = allSelected || someSelected;
  }

  @action
  updateCategoryState(category: Category) {
    const allSelected = category.subCategories.every(sc => 
      sc.items.every(i => this.selectedTools.includes(i.id))
    );
    const someSelected = category.subCategories.some(sc => 
      sc.items.some(i => this.selectedTools.includes(i.id))
    );
    category.selected = allSelected || someSelected;
  }

  getSubCategoryTokens(subCategory: SubCategory): number {
    return subCategory.items
      .filter(item => this.selectedTools.includes(item.id) && item.tokens !== undefined)
      .reduce((sum, item) => sum + (item.tokens || 0), 0);
  }

  getCategoryTokens(category: Category): number {
    return category.subCategories.reduce((sum, subCategory) => 
      sum + this.getSubCategoryTokens(subCategory), 0
    );
  }

  /** Set the approval mode (not set / ask / allow / deny) for an MCP tool */
  @action
  async setApprovalMode(toolName: string, mode: McpToolAllowMode | null) {
    const item = this.findItem(toolName);
    if (!item) return;
    // Optimistic update — the server response is authoritative for errors (toast)
    item.allowMode = mode;
    await apiService.post('/api/tools/set-allow-mode', { toolName, allowMode: mode });
  }

  private findItem(toolName: string): ToolItem | undefined {
    for (const category of this.categories) {
      for (const subCategory of category.subCategories) {
        const item = subCategory.items.find(i => i.id === toolName);
        if (item) return item;
      }
    }
    return undefined;
  }
}

type ChatToolsTabProps = {
  tools?: string[] | null;
  loader: PromiseCompletion;
  onChange?: (selectedTools: string[]) => void;
}

@observer
class ChatToolsTab extends Component<ChatToolsTabProps> {
  @observable store = new ChatToolsTabStore({ tools: this.props.tools, loader: this.props.loader, onChange: this.props.onChange });

  constructor(props: ChatToolsTabProps) {
    super(props);
    makeObservable(this);
  }

  render() {
    if (this.store.isLoading) {
      return <BlockLoader isVisible size="sm" style={{ minHeight: 120 }} />;
    }
    return (
      <div className="chat-tools-tab">
        {/*<div className="chat-tools-tab-header">
          <h3>Select Tools</h3>
          <span className="selected-count">
            {this.store.selectedTools.length} selected
          </span>
        </div>*/}
        <div className="chat-tools-tab-content">
          {this.store.categories.map(category => this.renderCategory(category))}
        </div>
      </div>
    );
  }

  renderCategory(category: Category) {
    const hasSelection = category.subCategories.some(sc => 
      sc.items.some(i => this.store.selectedTools.includes(i.id))
    );
    const allSelected = category.subCategories.every(sc => 
      sc.items.every(i => this.store.selectedTools.includes(i.id))
    );
    const categoryTokens = this.store.getCategoryTokens(category);

    return (
      <div key={category.id} className="category">
        <div className="category-header">
          <button
            className="expand-btn"
            onClick={() => this.store.toggleCategory(category.id)}
          >
            <ChevronRight expanded={category.expanded} />
          </button>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => {
                if (el) el.indeterminate = hasSelection && !allSelected;
              }}
              onChange={() => this.store.toggleCategorySelection(category.id)}
            />
            <ToolIcon icon={category.icon} />
            <span className="title">
              {category.name}
              {categoryTokens > 0 && (
                <span className="token-count"> ({formatTokenCount(categoryTokens)} tokens, estimated)</span>
              )}
            </span>
          </label>
        </div>
        <div className={'category-content' + (category.expanded ? ' expanded' : '')}>
          <div className="collapsible-inner">
            {category.subCategories.map(subCategory => 
              this.renderSubCategory(category.id, subCategory)
            )}
          </div>
        </div>
      </div>
    );
  }

  renderSubCategory(categoryId: string, subCategory: SubCategory) {
    const hasSelection = subCategory.items.some(i => this.store.selectedTools.includes(i.id));
    const allSelected = subCategory.items.every(i => this.store.selectedTools.includes(i.id));
    const subCategoryTokens = this.store.getSubCategoryTokens(subCategory);

    const category = this.store.categories.find(c => c.id === categoryId);
    return (
      <div key={subCategory.id} className="subcategory">
        <div className="subcategory-header">
          <button
            className="expand-btn"
            onClick={() => this.store.toggleSubCategory(categoryId, subCategory.id)}
          >
            <ChevronRight expanded={subCategory.expanded} />
          </button>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => {
                if (el) el.indeterminate = hasSelection && !allSelected;
              }}
              onChange={() => this.store.toggleSubCategorySelection(categoryId, subCategory.id)}
            />
            <ToolIcon icon={subCategory.icon ?? category?.icon} />
            <span className="title">
              {subCategory.name}
              {subCategoryTokens > 0 && (
                <span className="token-count"> ({formatTokenCount(subCategoryTokens)} tokens)</span>
              )}
            </span>
          </label>
        </div>
        <div className={'subcategory-content' + (subCategory.expanded ? ' expanded' : '')}>
          <div className="collapsible-inner">
            {subCategory.items.map(item => 
              this.renderItem(categoryId, subCategory.id, item)
            )}
          </div>
        </div>
      </div>
    );
  }

  renderItem(categoryId: string, subCategoryId: string, item: ToolItem) {
    const category = this.store.categories.find(c => c.id === categoryId);
    const subCategory = category?.subCategories.find(sc => sc.id === subCategoryId);
    return (
      <div key={item.id} className="item">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={this.store.selectedTools.includes(item.id)}
            onChange={() => this.store.toggleItemSelection(categoryId, subCategoryId, item.id)}
          />
          <ToolIcon icon={item.icon ?? subCategory?.icon ?? category?.icon} />
          <span className="title">
            {item.name}
            <span className="description" title={item.description}>{item.description}</span>
          </span>
          {item.isMcp && (
            <select
              className={`approval-mode approval-mode--${item.allowMode ?? 'unset'}`}
              title="Approval mode"
              value={item.allowMode ?? ''}
              onClick={e => e.stopPropagation()}
              onChange={e => this.store.setApprovalMode(item.id, (e.target.value || null) as McpToolAllowMode | null)}
            >
              <option value="">Not set</option>
              <option value="ask">Ask</option>
              <option value="allow">Always allow</option>
              <option value="deny">Deny</option>
            </select>
          )}
          {item.definition && (
            <button
              className="tool-def-btn"
              title="View tool definition"
              onClick={e => { e.stopPropagation(); this.showDefinition(item); }}
            >
              <ImgCodeBraces size={14} />
            </button>
          )}
        </label>
      </div>
    );
  }

  private showDefinition(item: ToolItem) {
    void modalService.show(ToolDefinitionDialog, { title: item.name, definition: item.definition });
  }
}

export default ChatToolsTab;